import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';

// --- Types ---

interface Notification {
  id: number;
  type: 'contract_expiry' | 'visit_reminder' | 'new_message' | 'payment_due' | 'listing_expired' | 'system';
  title: string;
  body: string;
  is_read: boolean;
  created_at: string;
}

interface NotificationsResponse {
  data: Notification[];
  meta?: {
    total: number;
    page: number;
    page_size: number;
  };
}

// --- Constants ---

const TYPE_ICON: Record<Notification['type'], string> = {
  contract_expiry: '\uD83D\uDCC4',
  visit_reminder: '\uD83D\uDCC5',
  new_message: '\uD83D\uDCAC',
  payment_due: '\uD83D\uDCB3',
  listing_expired: '\uD83C\uDFE0',
  system: '\u2699\uFE0F',
};

const TYPE_LABEL: Record<Notification['type'], string> = {
  contract_expiry: 'Contrato',
  visit_reminder: 'Visita',
  new_message: 'Mensaje',
  payment_due: 'Pago',
  listing_expired: 'Publicacion',
  system: 'Sistema',
};

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMin < 1) return 'Justo ahora';
  if (diffMin < 60) return `Hace ${diffMin} min`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays < 7) return `Hace ${diffDays}d`;
  return date.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
}

// --- Component ---

export default function NotificationsPage(): React.ReactElement {
  const queryClient = useQueryClient();

  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success',
  });

  // Auto-hide toast
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast((s) => ({ ...s, show: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  // --- Queries ---

  const { data: notificationsData, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<NotificationsResponse>('/v1/notifications'),
  });

  const notifications = notificationsData?.data ?? [];
  const unreadCount = notifications.filter((n) => !n.is_read).length;

  // --- Mutations ---

  const markReadMutation = useMutation({
    mutationFn: (id: number) => api.patch(`/v1/notifications/${id}/read`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
    onError: () => {
      setToast({ show: true, message: 'Error al marcar la notificacion', type: 'error' });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => api.patch('/v1/notifications/read-all', {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      setToast({ show: true, message: 'Todas las notificaciones marcadas como leidas', type: 'success' });
    },
    onError: () => {
      setToast({ show: true, message: 'Error al marcar las notificaciones', type: 'error' });
    },
  });

  const handleNotificationClick = (notification: Notification): void => {
    if (!notification.is_read) {
      markReadMutation.mutate(notification.id);
    }
  };

  // --- Render ---

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold">Notificaciones</h1>
          <p className="text-sm text-base-content/60">
            {unreadCount > 0
              ? `${unreadCount} sin leer`
              : 'No hay notificaciones sin leer'}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => markAllReadMutation.mutate()}
            disabled={markAllReadMutation.isPending}
          >
            {markAllReadMutation.isPending ? (
              <span className="loading loading-spinner loading-sm" />
            ) : (
              'Marcar todas como leidas'
            )}
          </button>
        )}
      </div>

      {/* Loading */}
      {isLoading ? (
        <div className="flex justify-center p-8">
          <span className="loading loading-spinner loading-lg" />
        </div>
      ) : notifications.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-4xl mb-4">&#128276;</p>
          <p className="text-lg text-base-content/60">
            No hay notificaciones todavia
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`card bg-base-100 shadow-sm cursor-pointer transition-colors hover:bg-base-200 ${
                !notification.is_read ? 'border-l-4 border-l-primary' : ''
              }`}
              onClick={() => handleNotificationClick(notification)}
            >
              <div className="card-body p-4">
                <div className="flex items-start gap-3">
                  {/* Type icon */}
                  <div className="text-2xl flex-shrink-0 mt-0.5">
                    {TYPE_ICON[notification.type]}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className={`font-semibold truncate ${!notification.is_read ? 'text-base-content' : 'text-base-content/70'}`}>
                            {notification.title}
                          </h3>
                          <span className="badge badge-xs badge-ghost flex-shrink-0">
                            {TYPE_LABEL[notification.type]}
                          </span>
                        </div>
                        <p className={`text-sm mt-0.5 ${!notification.is_read ? 'text-base-content/80' : 'text-base-content/50'}`}>
                          {notification.body}
                        </p>
                      </div>

                      {/* Time + read indicator */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-base-content/50 whitespace-nowrap">
                          {timeAgo(notification.created_at)}
                        </span>
                        {!notification.is_read && (
                          <span className="w-2.5 h-2.5 rounded-full bg-primary flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Toast */}
      {toast.show && (
        <div className="toast toast-end toast-bottom z-50">
          <div className={`alert ${toast.type === 'success' ? 'alert-success' : 'alert-error'}`}>
            <span>{toast.message}</span>
            <button
              className="btn btn-ghost btn-xs"
              onClick={() => setToast((s) => ({ ...s, show: false }))}
            >
              &#10005;
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
